import { TRESPONSE_LotListing, TStockType } from '@monorepo/types';
import { ModifiersZTableModel } from '../table.modifier';
import { TTableCellTree } from '../table.type';
import { LOT_TABLE_MODEL } from './ba-app/LOT_TABLE_MODEL';

export const FABRIC_LOT_MODEL = {
  createModel: (
    items: TRESPONSE_LotListing['items'],
    headerConfig: TRESPONSE_LotListing['headerConfig'],
    supportedFilters: TRESPONSE_LotListing['supportedFilters'],
    isColorFieldEnabled: boolean,
    stockType: TStockType,
  ) => {
    const isPlmExists = items.length > 0 && !!items[0].metadata.plm;
    const isPackedFilterAvailable = supportedFilters.find((filter) => filter.filterType === 'PACKED');
    return ModifiersZTableModel.createTableModel(
      items,
      {
        headerConfig: [
          {
            type: 'TEXT_CELL',
            text: 'STATUS',
            stickyType: 'LEFT',
          },
          {
            type: 'TEXT_CELL',
            text: LOT_TABLE_MODEL.getHeaderName(headerConfig, 'supplierName') || 'SUPPLIER',
          },

          ...(isPlmExists
            ? [
                {
                  type: 'TEXT_CELL',
                  text: LOT_TABLE_MODEL.getHeaderName(headerConfig, 'plm') || 'PLM NO.',
                } as TTableCellTree,
              ]
            : []),
          ...(stockType === 'with_bctx'
            ? ([
                {
                  type: 'TEXT_CELL',
                  text: LOT_TABLE_MODEL.getHeaderName(headerConfig, 'buyer') || 'BUYER',
                },
                {
                  type: 'TEXT_CELL',
                  text: LOT_TABLE_MODEL.getHeaderName(headerConfig, 'po') || 'PO NO.',
                },
                {
                  type: 'TEXT_CELL',
                  text: LOT_TABLE_MODEL.getHeaderName(headerConfig, 'style') || 'STYLE',
                },
                {
                  type: 'TEXT_CELL',
                  text: LOT_TABLE_MODEL.getHeaderName(headerConfig, 'materialCode') || 'Material Code / PDM',
                },
              ] as TTableCellTree[])
            : ([
                {
                  type: 'TEXT_CELL',
                  text: LOT_TABLE_MODEL.getHeaderName(headerConfig, 'itemName') || 'ITEM NAME',
                },
                {
                  type: 'TEXT_CELL',
                  text: LOT_TABLE_MODEL.getHeaderName(headerConfig, 'itemCode') || 'ITEM CODE',
                },
                {
                  type: 'TEXT_CELL',
                  text: LOT_TABLE_MODEL.getHeaderName(headerConfig, 'lotGrnNumbers') || 'GRN No.',
                },
              ] as TTableCellTree[])),

          // Show packed column if packed filter is available
          ...(isPackedFilterAvailable
            ? [
                {
                  type: 'TEXT_CELL',
                  text: LOT_TABLE_MODEL.getHeaderName(headerConfig, 'packed') || 'PACKED',
                } as TTableCellTree,
              ]
            : []),

          {
            type: 'TEXT_CELL',
            text: LOT_TABLE_MODEL.getHeaderName(headerConfig, 'fabricVariant') || 'FABRIC VARIANT',
          },
          ...(isColorFieldEnabled && stockType === 'with_bctx'
            ? [
                {
                  type: 'TEXT_CELL',
                  text: LOT_TABLE_MODEL.getHeaderName(headerConfig, 'color') || 'COLOR',
                } as TTableCellTree,
              ]
            : []),
          {
            type: 'TEXT_CELL',
            text: LOT_TABLE_MODEL.getHeaderName(headerConfig, 'fabricColor') || 'FABRIC COLOR',
          },
          {
            type: 'TEXT_CELL',
            text: LOT_TABLE_MODEL.getHeaderName(headerConfig, 'fabricType') || 'FAB TYPE',
          },
          {
            type: 'TEXT_CELL',
            text: LOT_TABLE_MODEL.getHeaderName(headerConfig, 'lotNo') || 'LOT NO/BATCH NO.',
          },
        ],
        select: {
          position: 'END',
          isMulti: false,
          size: 'LARGE',
          stickyType: 'RIGHT',
        },
      },
      (item) => {
        return {
          type: 'ROW',
          rowItem: item,
          isSelected: false,
          cells: [
            {
              type: 'TEXT_CELL',
              text: item.status,
            },
            {
              type: 'TEXT_CELL',
              text: item.metadata.supplierName,
            },

            ...(isPlmExists
              ? [
                  {
                    type: 'TEXT_CELL',
                    text: item.metadata.plm,
                  } as TTableCellTree,
                ]
              : []),
            ...(stockType === 'with_bctx'
              ? ([
                  {
                    type: 'TEXT_CELL',
                    text: item.metadata.buyer,
                  },
                  {
                    type: 'TEXT_CELL',
                    text: item.metadata.po,
                  },
                  {
                    type: 'TEXT_CELL',
                    text: item.metadata.style,
                  },
                  {
                    type: 'TEXT_CELL',
                    text: item.metadata.materialCode,
                  },
                ] as TTableCellTree[])
              : ([
                  {
                    type: 'TEXT_CELL',
                    text: item.metadata.itemName,
                  },
                  {
                    type: 'TEXT_CELL',
                    text: item.metadata.itemCode,
                  },
                  {
                    type: 'TEXT_CELL',
                    tag: {
                      tagContent:
                        item.lotGrnNumbers && item.lotGrnNumbers.length > 0
                          ? item.lotGrnNumbers.length > 1
                            ? `${item.lotGrnNumbers[0]} + ${item.lotGrnNumbers.length - 1} more`
                            : item.lotGrnNumbers[0]
                          : '--',
                      ...(item.lotGrnNumbers &&
                        item.lotGrnNumbers.length > 1 && {
                          tagTooltipContent: item.lotGrnNumbers.join(', '),
                        }),
                    },
                  },
                ] as TTableCellTree[])),

            ...(isPackedFilterAvailable
              ? [
                  {
                    type: 'TEXT_CELL',
                    text: item.metadata.packed ?? '--',
                  } as TTableCellTree,
                ]
              : []),

            {
              type: 'TEXT_CELL',
              text: item.metadata.fabricVariant,
            },
            ...(isColorFieldEnabled && stockType === 'with_bctx'
              ? [
                  {
                    type: 'TEXT_CELL',
                    text: item.metadata.color,
                  } as TTableCellTree,
                ]
              : []),
            {
              type: 'TEXT_CELL',
              text: item.metadata.fabricColor,
            },
            {
              type: 'TEXT_CELL',
              text: item.metadata.fabricType,
            },
            {
              type: 'TEXT_CELL',
              text: item.metadata.lotNo,
            },
          ],
        };
      },
    );
  },
};
